<template>
  <div class="wallet-connect">
    <button
      v-if="!isConnected"
      @click="showSelector = true"
      :disabled="isConnecting"
      class="wallet-connect-button"
      :class="{ 'connecting': isConnecting, 'compact': compact }"
    >
      <span v-if="isConnecting">{{ $t('wallet.connecting') }}</span>
      <span v-else-if="compact">{{ $t('nav.connect') }}</span>
      <span v-else>{{ $t('wallet.connect') }}</span>
    </button>

    <div v-else class="wallet-connected" :class="{ 'compact': compact }">
      <span class="wallet-address">{{ formatAddress(address) }}</span>
      <button @click="handleDisconnect" class="wallet-disconnect-button" :class="{ 'compact': compact }">
        {{ compact ? $t('nav.disconnect') : $t('wallet.disconnect') }}
      </button>
    </div>

    <div v-if="error" class="wallet-error">
      {{ error }}
    </div>

    <!-- 錢包選擇器模態框 -->
    <WalletSelector :is-open="showSelector" @close="showSelector = false" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useWalletStore } from '../stores/wallet'
import { useI18n } from 'vue-i18n'
import WalletSelector from './WalletSelector.vue'
import { logger } from '../utils/logger'

interface Props {
  compact?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  compact: false
})

const { t } = useI18n()
const walletStore = useWalletStore()

// 從 store 獲取狀態
const isConnected = computed(() => walletStore.isConnected)
const isConnecting = computed(() => walletStore.isConnecting)
const address = computed(() => walletStore.address)
const chainId = computed(() => walletStore.chainId)
const error = computed(() => walletStore.error)

// 顯示選擇器
const showSelector = ref(false)

// 初始化（延遲初始化，只在需要時才初始化 WalletConnect）
onMounted(async () => {
  // 不自動初始化 WalletConnect，只在用戶選擇 WalletConnect 時才初始化
  // 這樣可以避免自動彈出 WalletConnect 模態框
})

// 斷開連接
async function handleDisconnect() {
  try {
    await walletStore.disconnect()
  } catch (err) {
    logger.error('斷開連接失敗:', err)
  }
}

// 格式化地址
function formatAddress(addr: string | null): string {
  if (!addr) return ''
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`
}
</script>

<style scoped>
.wallet-connect {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.wallet-connect-button {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
  color: white;
  border: none;
  border-radius: 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-primary);
}

.wallet-connect-button.compact {
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
}

.wallet-connect-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.wallet-connect-button:disabled,
.wallet-connect-button.connecting {
  opacity: 0.6;
  cursor: not-allowed;
}

.wallet-connected {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 0.75rem;
}

.wallet-connected.compact {
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem;
}

.wallet-address {
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-primary);
}

.wallet-connected.compact .wallet-address {
  font-size: 0.75rem;
}

.wallet-disconnect-button {
  padding: 0.5rem 1rem;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid rgba(156, 163, 175, 0.3);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.wallet-disconnect-button.compact {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

.wallet-disconnect-button:hover {
  background: #dc2626;
}

.wallet-error {
  padding: 0.75rem;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}
</style>

